import React, { useMemo } from 'react'

import {
  BenefitsWrapper,
  BenefitContainer,
  BenefitInfoContainer,
  BenefitTitle,
  BenefitIcon,
  BenefitSubtext,
} from './styled'

import {
  SectionHeader,
  SectionHeaderContainerProps,
} from '../../components/SectionHeader'

import { DashboardLinksProps, DashboardLinks } from '../../components/DashboardLinks'
import { sortRewardsByRank } from '../../utils/rankingUtils'

// =======================
// Types
// =======================
export interface BenefitProps {
  title: string
  subtext: string
  imageClass?: string
  hideBottomImage?: boolean
  dashboardLinks?: Array<DashboardLinksProps>
  rank: number
}

export interface BenefitsProps {
  sectionHeaderProps?: SectionHeaderContainerProps
  benefits?: Array<BenefitProps>
}

// =======================
// Component
// =======================
export const Benefits: React.FC<BenefitsProps> = ({
  sectionHeaderProps = { color: '#131313', borderColor: '#e8d088' },
  benefits = [SaveMoreBenefit, FasterCheckoutBenefit, BirthdayRewardBenefit],
}) => {
  // Create a stable, sorted copy (non-mutating)
  const ordered = useMemo(() => {
    const copy = [...benefits]
    sortRewardsByRank(copy)
    return copy
  }, [benefits])

  const titleLabel = useMemo(() => {
    if (!ordered.length) return 'Benefits'
    const maxRank = ordered.reduce((m, b) => (b.rank > m ? b.rank : m), ordered[0].rank)
    return maxRank < 4 ? 'More Great Benefits' : 'More Great Benefits'
  }, [ordered])

  return (
    <div>
      <SectionHeader
        label={titleLabel}
        color={sectionHeaderProps.color}
        borderColor={sectionHeaderProps.borderColor}
      />

      <BenefitsWrapper>
        {ordered.map((benefit) => (
          <BenefitContainer key={`${benefit.title}-${benefit.rank}`}>
            <BenefitInfoContainer>
              <BenefitTitle className="semi">
                <BenefitIcon imageName={benefit.imageClass} />
                {benefit.title}
              </BenefitTitle>
              <BenefitSubtext>{benefit.subtext}</BenefitSubtext>
            </BenefitInfoContainer>

            {benefit.dashboardLinks && benefit.dashboardLinks.length > 0 && (
              <DashboardLinks links={benefit.dashboardLinks} />
            )}
          </BenefitContainer>
        ))}
      </BenefitsWrapper>
    </div>
  )
}

// =======================
// Presets
// =======================
export const SaveMoreBenefit: BenefitProps = {
  title: 'Exclusive Offers',
  subtext: 'Get early access to new products and free or discounted items',
  imageClass: 'save-more',
  rank: 1,
}

export const FasterCheckoutBenefit: BenefitProps = {
  title: 'Faster checkout',
  subtext: 'Checkout fast by saving your payment and address preferences',
  imageClass: 'faster-checkout',
  rank: 2,
}

export const BirthdayRewardBenefit: BenefitProps = {
  title: 'Birthday reward',
  subtext: 'Celebrate with a special reward on your birthday',
  imageClass: 'birthday-reward',
  rank: 3,
}

export const DashboardSaveMoreBenefit: BenefitProps = {
  title: 'Exclusive offers',
  subtext: 'Keep an eye out for awesome deals and early access to new items',
  imageClass: 'save-more',
  hideBottomImage: true,
  rank: 5,
}

export const DashboardFasterCheckoutBenefit: BenefitProps = {
  title: 'Faster checkout',
  subtext: 'Checkout fast by saving your payment and address preferences',
  imageClass: 'faster-checkout',
  hideBottomImage: true,
  dashboardLinks: [],
  rank: 4,
}

export const DashboardBirthdayRewardBenefit: BenefitProps = {
  title: 'Birthday reward',
  subtext: 'Celebrate with a special reward on your birthday',
  imageClass: 'birthday-reward',
  hideBottomImage: true,
  rank: 6,
}
